import { BiDashboardHostContext } from '@euler/app/components/message/widgets/bi-dashboard-changes/BiDashboardHostContext';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { kBiDefaultPalette } from '@euler/model/bi/viz';
import { memo, useMemo, useState } from 'react';
import { ActivityIndicator, View, useWindowDimensions } from 'react-native';
import {
  BiPageHeader,
  kBiContentMaxWidth,
} from '../components/ui/BiPageHeader';
import { BiRetry } from '../components/ui/BiRetry';
import { DashboardChatPanel } from '../dashboard/chat/DashboardChatPanel';
import { kBiDashboardChatTool } from '../dashboard/chat/biDashboardChatTool';
import { CardEditorPanel } from '../dashboard/editor/CardEditorPanel';
import { useBiCardEditor } from '../dashboard/editor/functions/useBiCardEditor';
import { DateRangeBar } from '../dashboard/filter-bar/DateRangeBar';
import {
  BiDateChoice,
  biEffectiveRange,
  biRequestFilters,
} from '../dashboard/functions/biDateRange';
import { UndoToast } from '../dashboard/undo/UndoToast';
import { CardConfigPane } from './CardConfigPane';
import { CardDetailActions } from './CardDetailActions';
import { CardDetailColumn } from './CardDetailColumn';
import { biCardShown } from './functions/biCardShown';
import { useBiCardDetail } from './functions/useBiCardDetail';

/** From this width the configuration sits beside the card. */
const kTwoColumnsFrom = 1024;
/** From this width the chat sits beside both columns. */
const kChatBesideFrom = 1280;

/**
 * One card on its own page: the card and its rows; beside it on desktop,
 * its configuration with a live preview; on a phone, 配置 opens the same
 * form as a sheet. Viewers see it read-only, a restricted card stays so.
 */
export const BiCardDetailView = memo(
  ({
    dashboardId,
    cardId,
    choice,
    onChoice,
    onBack,
  }: {
    dashboardId: string;
    cardId: string;
    /** The date range shown, opened as the dashboard was showing it. */
    choice: BiDateChoice;
    onChoice: (choice: BiDateChoice) => void;
    onBack: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const compact = useIsMobileLayout();
    const { width } = useWindowDimensions();
    const twoColumns = width >= kTwoColumnsFrom;
    const chatBeside = width >= kChatBesideFrom;
    const filters = useMemo(() => biRequestFilters(choice), [choice]);
    const detail = useBiCardDetail(dashboardId, cardId, chatBeside, filters);
    const { dashboard, card, data, chat, target, viewed, save } = detail;
    const [sheet, setSheet] = useState(false);
    const editor = useBiCardEditor(twoColumns ? target : null, save, viewed);
    const palette = dashboard?.palette ?? kBiDefaultPalette;
    const shown = card
      ? biCardShown(card, data, twoColumns && target ? editor : null)
      : null;
    return (
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          backgroundColor: aiColors.background,
        }}
      >
        <View style={{ flex: 1 }}>
          <BiPageHeader
            title={dashboard?.name ?? '数据看板'}
            onBack={onBack}
            actions={
              <CardDetailActions
                compact={compact}
                showChat={!!card && !(chatBeside && chat.visible)}
                onChat={() => chat.setVisible(true)}
                onConfigure={
                  !twoColumns && detail.canConfigure
                    ? () => setSheet(true)
                    : null
                }
              />
            }
          >
            {dashboard ? (
              <DateRangeBar
                choice={choice}
                saved={dashboard.filters}
                onChange={onChoice}
              />
            ) : null}
          </BiPageHeader>
          {!shown || !card ? (
            <View
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {detail.state.kind === 'loading' ? (
                <ActivityIndicator color={aiColors.primary} />
              ) : detail.state.kind === 'failed' ? (
                <BiRetry onRetry={detail.refresh} />
              ) : (
                <BiRetry
                  label="图表不存在，或你没有查看权限"
                  onRetry={onBack}
                />
              )}
            </View>
          ) : (
            // The card and its configuration share the header's column:
            // its padding, maximum width and centering.
            <View style={{ flex: 1, paddingHorizontal: rpx(16) }}>
              <View
                style={{
                  flex: 1,
                  width: '100%',
                  maxWidth: kBiContentMaxWidth,
                  alignSelf: 'center',
                  flexDirection: 'row',
                  gap: rpx(16),
                }}
              >
                <CardDetailColumn
                  shown={shown}
                  palette={palette}
                  dashboardFiltered={
                    !!dashboard && !!biEffectiveRange(choice, dashboard.filters)
                  }
                  chartHeight={rpx(twoColumns ? 320 : 240)}
                  onRetry={detail.retry}
                />
                {twoColumns && target ? (
                  <CardConfigPane
                    editor={editor}
                    palette={palette}
                    onCancel={detail.cancel}
                  />
                ) : null}
              </View>
            </View>
          )}
          {!twoColumns ? (
            <CardEditorPanel
              target={sheet ? target : null}
              filters={viewed}
              palette={palette}
              onSave={save}
              onClose={() => setSheet(false)}
            />
          ) : null}
        </View>
        <BiDashboardHostContext.Provider value={detail.host}>
          {chat.visible || chat.opened ? (
            <DashboardChatPanel
              tool={kBiDashboardChatTool}
              side={chatBeside}
              visible={chat.visible}
              conversation={chat.conversation}
              state={chat.state}
              tapSettled$={chat.tapSettled$}
              onClose={() => chat.setVisible(false)}
            />
          ) : null}
        </BiDashboardHostContext.Provider>
        {dashboard ? (
          <UndoToast
            toast={detail.toast}
            revision={dashboard.revision}
            canUndo={detail.canUndo}
            onUndo={detail.onUndo}
            onDismiss={detail.dismissToast}
          />
        ) : null}
      </View>
    );
  },
);
